/*  EME wrapper.
 *   Set up Encrypted Media Extensions
 */

import { ErrorTypes, ErrorDetails } from '../hlsjs/errors';
import Event from '../hlsjs/events';
import EventHandler from '../hlsjs/event-handler';
import EME from './EME';
import KeySystem from '../KeySystems';
import { logger } from '../hlsjs/utils/logger';
import LegacyEME from './LegacyEME';
import ModernEME from './ModernEME';
import muxjs from '../muxjs';

class LicenseManager extends EventHandler {
	constructor(hls, obtainKeySystemConfigCallback) {
		super(hls,
			Event.MEDIA_ATTACHING,
			Event.MEDIA_DETACHING,
			Event.DESTROYING,
			Event.BUFFER_CREATED
		);

		this._obtainKeySystemConfigCallback = obtainKeySystemConfigCallback;
	}

	get drmCapabilities() {
		return EME.capabilities;
	}

	onMediaAttaching(data) {
		this._video = data.media;
	}

	onDestroying() {
		this._obtainKeySystemConfigCallback = null;
	}

	onMediaDetaching() {
		if(this._drm) {
			this._drm.dispose();
		}

		this._drm = null;
		this._video = null;
	}

	onLicenseManagerError(err) {
		logger.error(`license manager error: ${err.message}`);
		if (this.hls) {
			this.hls.trigger(Event.ERROR, {
				type: ErrorTypes.MEDIA_ERROR,
				details: ErrorDetails.DRM_ERROR,
				fatal: true,
				reason: err.message,
			});
		}
	}

	extractSkd(levelkeys) {
		let i;
		let initData = null;

		if(levelkeys) {
			for(i = 0; i < levelkeys.length && initData === null; i++) {
				if (levelkeys[i].format === 'com.apple.streamingkeydelivery') {
					let str = levelkeys[i].reluri;

					let base64IndexStart = 6; //skd://
					let base64IndexEnd = str.indexOf(';');

					if (base64IndexEnd < 0) {
						base64IndexEnd = str.length;
					}

					let j;
					let base64 = str.substring(base64IndexStart, base64IndexEnd);
					initData = new Uint8Array(base64.length);

					for(j = 0; j < base64.length; j++) {
						initData[j] = base64.charCodeAt(j);
					}
				}
			}
		}

		return initData;
	}

	hasSampleAes(initFrag) {
		let hasSampleAes = false;
		let i;

		let frag = muxjs.mp4.tools.inspect(new Uint8Array(initFrag));

/*
		//Need to implement this. muxjs.mp4.tools.inspect does not have full support for all these atoms.
		//For now we will look for the pssh box to determine encryption. We won't find a pssh box under Safari
		//so for now we will look for a stream type of enca or encv.

		Protection scheme signaling conforms with ISO Base media File Format [BMFF]. When protection has been applied, the stream type will be
		transformed to 'encv' for video or 'enca' for audio, with a Protection Scheme Information Box ('sinf') added to the sample entry in the
		Sample Description Box ('stsd'). The Protection Scheme Information Box ('sinf') will contain a Scheme Type Box ('schm') with a scheme_type
		field set to a value of 'cenc' (Common Encryption).
		The "encrypted block" is a sample. Determining whether a sample is encrypted depends on the corresponding Track Encryption Box ('tenc') and the sample
		group with grouping type 'seig' (CencSampleEncryption group), if any, associated with the sample. The default encryption state of a sample is defined by
		the IsEncrypted flag in the associated track encryption box ('tenc'). This default state may be modified by the IsEncrypted flag in the
		SampleGroupDescriptionBox ('sgpd'), pointed to by an index in the SampleToGroupBox ('sbgp').
		Samples can be partially encrypted, specified by subsample information referenced by SampleAuxiliaryInformationSizesBox ('saiz')
		and SampleAuxiliaryInformationOffsetsBox ('saio') boxes.
*/

		for (i = 0; i < frag.length && hasSampleAes === false; i++) {
			if (frag[i].type === 'moov') {
				let j;
				for (j = 0; j < frag[i].boxes.length && hasSampleAes === false; j++) {
					if (frag[i].boxes[j].type === 'pssh') {
						logger.log(`hasSampleAes - pssh found`);
						hasSampleAes = true;
					}
					//don't bother with all this if we are not Safari
					else if (this.hls.browSafari && frag[i].boxes[j].type === 'trak') {
						let k;
						for (k = 0; k < frag[i].boxes[j].boxes.length && hasSampleAes === false; k++) {
							if (frag[i].boxes[j].boxes[k].type === 'mdia') {
								let l;
								for (l = 0; l < frag[i].boxes[j].boxes[k].boxes.length && hasSampleAes === false; l++) {
									if (frag[i].boxes[j].boxes[k].boxes[l].type === 'minf') {
										let m;
										for (m = 0; m < frag[i].boxes[j].boxes[k].boxes[l].boxes.length && hasSampleAes === false; m++) {
											if (frag[i].boxes[j].boxes[k].boxes[l].boxes[m].type === 'stbl') {
												let n;
												for (n = 0; n < frag[i].boxes[j].boxes[k].boxes[l].boxes[m].boxes.length && hasSampleAes === false; n++) {
													if (frag[i].boxes[j].boxes[k].boxes[l].boxes[m].boxes[n].type === 'stsd') {
														let sampleDescriptions = frag[i].boxes[j].boxes[k].boxes[l].boxes[m].boxes[n].sampleDescriptions;
														if (sampleDescriptions) {
															let o;
															for(o = 0; o < sampleDescriptions.length && hasSampleAes === false; o++) {
																if(sampleDescriptions[o].type === 'enca' || sampleDescriptions[o].type === 'encv') {
																	logger.log(`hasSampleAes - sample description type: ${sampleDescriptions[o].type}`);
																	hasSampleAes = true;
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}

		return hasSampleAes;
	}

	onBufferCreated(data) {
		logger.log(`LicenseManager onBufferCreated`);
		let tracks = data.tracks;
		let encryptedCount = 0;
		let skd = null;

		if(!this._drm) {
			let options = [{
				initDataTypes: ['cenc'],
			}];

			if(tracks.audio) {
				if (this.hasSampleAes(tracks.audio.initSegment)) {
					//change name of setOptions, it's confusing ... should be buildOptions/createOptions
					options = this.setOptions(tracks.audio, options);

					//this is a temp solution ... should really be building this from the tenc atom??
					//this relies on the skd being the same ... this will be overwritten in the next if block
					skd = this.extractSkd(tracks.audio.levelkeys);

					encryptedCount++;
				}
			}

			if(tracks.video) {
				if (this.hasSampleAes(tracks.video.initSegment)) {
					options = this.setOptions(tracks.video, options);

					//this is a temp solution ... should really be building this from the tenc atom??
					//this overwrites the previously set skd which is incorrect which means if the skd are different it will break
					//this really needs to be built from the tenc atom which is passed to us in onNeedKey (LegacyEME.js)
					skd = this.extractSkd(tracks.video.levelkeys);

					encryptedCount++;
				}
			}

			if(encryptedCount > 0) {
				if(window.navigator.requestMediaKeySystemAccess) {
					//need to factor in clearkey here by checking for identity key in the level manifest key tag

					if(this.hls.browEdge || this.hls.browTizen3) {
						logger.log(`PlayReady ModernEME`);
						this._drm = new ModernEME(this.hls, KeySystem.PLAYREADY, this._obtainKeySystemConfigCallback, this._video, encryptedCount);
					}
					else if(this.hls.browChrome || this.hls.browFirefox || this.hls.browTizen3) {
						logger.log(`Widevine ModernEME`);
						this._drm = new ModernEME(this.hls, KeySystem.WIDEVINE, this._obtainKeySystemConfigCallback, this._video, encryptedCount);
					}
					else {
						this.onLicenseManagerError({ message: 'unsupported browser' });
					}
				}
				else {
					if(this.hls.browIE || this.hls.browTizen2) {
						logger.log(`PlayReady LegacyEME`);
						this._drm = new LegacyEME(this.hls, KeySystem.PLAYREADY, this._obtainKeySystemConfigCallback, this._video, encryptedCount);
					}
					else if(this.hls.browSafari) {
						logger.log(`FairPlay LegacyEME`);
						this._drm = new LegacyEME(this.hls, KeySystem.FAIRPLAY, this._obtainKeySystemConfigCallback, this._video, encryptedCount);
					}
					else {
						this.onLicenseManagerError({ message: 'unsupported browser' });
					}
				}

				if (this._drm) {
					this._drm.setOptions(options);
					let keySystem = this._drm.keySystemName;

					//this will not work when multiple keys are in play
					//we would need to have a map based on the kid, then we would need to parse the tenc atom from the initSegment passed to us in onNeedKey
					//then we would have to extract the kid from that and then pull out the correct asset id from the generated map
					if(keySystem === KeySystem.FAIRPLAY) {
						this._drm.initData = skd;
					}

					//data - pass in data here and extract the relrui internally, we can also add the initData injection with a legacyEME derived function
					this._drm.setKeySystemConfig(null);
				}
			}
		}
	}

	setOptions(data, options) {
		let newOptions = options;

		if(data.container === 'audio/mp4') {
			let audioContentType = data.container;

			if(data.codec) {
				audioContentType += ('; codecs="' + data.codec + '"');
			}

			newOptions[0].audioCapabilities = [{
				contentType: audioContentType,
			}];
		}
		else if(data.container === 'video/mp4') {
			let videoContentType = data.container;

			if(data.codec) {
				videoContentType += ('; codecs="' + data.codec + '"');
			}

			newOptions[0].videoCapabilities = [{
				contentType: videoContentType,
			}];
		}

		return newOptions;
	}
}

export default LicenseManager;



// WEBPACK FOOTER //
// ./src/drm/LicenseManager.js